import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useEmployeeProfile } from "@/hooks/use-employee-profile";
import { useImpersonation } from "@/contexts/ImpersonationContext";
import { useRBAC } from "@/hooks/use-rbac";
import { EmployeeHeader } from "@/components/employee/EmployeeHeader";
import { ProjectsTab, type ProjectFilter } from "@/components/employee/ProjectsTab";
import { TimeEntriesTab } from "@/components/employee/TimeEntriesTab";
import { SettingsTab } from "@/components/employee/SettingsTab";
import { AktivitetTab } from "@/components/employee/AktivitetTab";
import { MaalTab } from "@/components/employee/MaalTab";
import { TimebankTab } from "@/components/employee/TimebankTab";
import { FravaerTab } from "@/components/employee/FravaerTab";
import { EmployeeKpiSection } from "@/components/employee/EmployeeKpiSection";
import { TabLoading } from "@/components/employee/TabLoading";
import { SrIcon } from "@/components/ui/SrIcon";

const tabTrigger =
  "rounded-none border-b-2 border-transparent bg-transparent px-3 py-2 text-muted-foreground shadow-none data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:text-primary data-[state=active]:shadow-none";

/**
 * Ansattkortet (VIB-3028). Fanene ligger rett under toppkortet. Admin åpner
 * på Nøkkeltall, alle andre på Prosjekter.
 */
export default function EmployeeDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { activeUser: currentUser } = useImpersonation();
  const { isAdmin } = useRBAC();
  const { data: profileData, isLoading, error } = useEmployeeProfile(id ?? null);
  const [activeTab, setActiveTab] = useState<string | null>(null);
  const [projectFilter, setProjectFilter] = useState<ProjectFilter>("active");

  const backLink = (
    <Link to="/ansatte" className="flex w-fit items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
      <SrIcon name="arrow-left" className="h-3.5 w-3.5" />
      Ansatte
    </Link>
  );

  if (isLoading) {
    return (
      <div className="page-container animate-fade-in">
        <TabLoading />
      </div>
    );
  }

  if (error || !profileData) {
    return (
      <div className="page-container space-y-4">
        {backLink}
        <p className={error ? "text-destructive" : "text-muted-foreground"}>
          {error ? `Kunne ikke hente ansatt: ${(error as Error).message}` : "Ansatt ikke funnet."}
        </p>
      </div>
    );
  }

  const { user, projects, timeEntries } = profileData;
  const canEdit = isAdmin || currentUser?.id === user.id;
  const tab = activeTab ?? (isAdmin ? "nokkeltall" : "prosjekter");

  const showProjects = (filter: ProjectFilter) => {
    setProjectFilter(filter);
    setActiveTab("prosjekter");
  };

  return (
    <div className="page-container animate-fade-in space-y-4">
      {backLink}
      <EmployeeHeader user={user} canChangeAvatar={isAdmin} />

      <Tabs value={tab} onValueChange={setActiveTab} className="space-y-4">
        <div className="overflow-x-auto border-b border-border">
          <TabsList aria-label="Faner på ansattkortet" className="h-auto justify-start gap-1 rounded-none bg-transparent p-0">
            {isAdmin && <TabsTrigger value="nokkeltall" className={tabTrigger}>Nøkkeltall</TabsTrigger>}
            <TabsTrigger value="prosjekter" className={tabTrigger}>
              Prosjekter
              <span className="ml-1 text-[11px] font-normal tabular-nums opacity-70">{projects.length}</span>
            </TabsTrigger>
            <TabsTrigger value="timer" className={tabTrigger}>Timer</TabsTrigger>
            <TabsTrigger value="maal" className={tabTrigger}>Mål</TabsTrigger>
            <TabsTrigger value="fravaer" className={tabTrigger}>Fravær</TabsTrigger>
            {/* VIB-2766: kun for admin. Timebanken er den ansattes egen, og en
                kollega har ikke noe å gjøre i den. RLS håndhever det samme i
                basen, så fanen er en bekvemmelighet, ikke grensa. */}
            {isAdmin && <TabsTrigger value="timebank" className={tabTrigger}>Timebank</TabsTrigger>}
            <TabsTrigger value="aktivitet" className={tabTrigger}>Aktivitet</TabsTrigger>
            {canEdit && <TabsTrigger value="innstillinger" className={tabTrigger}>Innstillinger</TabsTrigger>}
          </TabsList>
        </div>

        {isAdmin && (
          <TabsContent value="nokkeltall">
            <EmployeeKpiSection
              userId={user.id}
              targetBillingRate={user.target_billing_rate ?? null}
              onShowProjects={showProjects}
            />
          </TabsContent>
        )}

        <TabsContent value="prosjekter">
          <ProjectsTab
            userId={user.id}
            projects={projects}
            isAdmin={isAdmin}
            filter={projectFilter}
            onFilterChange={setProjectFilter}
          />
        </TabsContent>

        <TabsContent value="timer">
          <TimeEntriesTab timeEntries={timeEntries} />
        </TabsContent>

        <TabsContent value="maal">
          <MaalTab userId={user.id} targetBillingRate={user.target_billing_rate ?? null} />
        </TabsContent>

        <TabsContent value="fravaer">
          <FravaerTab userId={user.id} canManage={isAdmin} />
        </TabsContent>

        {isAdmin && (
          <TabsContent value="timebank">
            <TimebankTab userId={user.id} />
          </TabsContent>
        )}

        <TabsContent value="aktivitet">
          <AktivitetTab userId={user.id} />
        </TabsContent>

        {canEdit && (
          <TabsContent value="innstillinger">
            <SettingsTab key={user.id} user={user} showRates={isAdmin} />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
